import { TimerHelper } from './../../../helpers/timer.helper';
import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { Subscription } from 'rxjs';
import { SaveFormService } from '../../../services/save-form.service';
import { LocalizedName } from '../../../models/localized-name.model';
import { QuestionService } from '../../../services/question.service';
import { ConfigService } from '../../../services/config.service';
import {
  QuestionType,
  AdminQuestion,
  Answer,
  CompositeAnswerPortion,
} from '../../../models/questions';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-matrix-form',
  templateUrl: './matrix-form.component.html',
  styles: [],
})
export class MatrixFormComponent implements OnInit, OnDestroy {
  @Input() questionType: QuestionType;
  @Input() question: AdminQuestion;

  questionMatrixForm: FormGroup;
  emptyAnswerRow: Answer = {
    localizedNames: [],
    compositeAnswerPortion: CompositeAnswerPortion.Row,
  };
  emptyAnswerColumn: Answer = {
    localizedNames: [],
    compositeAnswerPortion: CompositeAnswerPortion.Column,
  };
  defaultColumnsArray: Array<Answer> = [
    this.emptyAnswerColumn,
    this.emptyAnswerColumn,
  ];
  defaultRowsArray: Array<Answer> = [this.emptyAnswerRow, this.emptyAnswerRow];
  titlePropertiesArray: Array<string> = [];
  columnsPropertiesArray: Array<Array<string>> = [];
  rowsPropertiesArray: Array<Array<string>> = [];
  partOfTitleControlName = 'title_';
  partOfMatrixColControlName = `columns`;
  partOfMatrixRowControlName = `rows`;
  titlePlaceholderArray: Array<string> = [];
  columnsPlaceholderArray: Array<string> = [];
  rowsPlaceholderArray: Array<string> = [];

  private subscriptions: Subscription = new Subscription();
  private fieldNumber: number;
  private saveClicked: boolean;

  constructor(
    private questionFormBuilder: FormBuilder,
    private appData: ConfigService,
    private questionService: QuestionService,
    private saveFormService: SaveFormService,
    private readonly toastr: ToastrService
  ) {
    this.subscriptions = this.saveFormService.saveButtonClicked$.subscribe(
      (x) => {
        if (x.question.id !== this.question.id) {
          return;
        }
        this.saveMatrixForm(x.isAdd);
      }
    );
    this.subscriptions.add(this.addInputSubscription());
  }

  ngOnInit() {
    if (!this.question) {
      this.question = {
        answerType: this.questionType,
        answers: [this.emptyAnswerColumn, this.emptyAnswerRow],
      };
    }
    this.createQuestionForm();
    this.question.answers = this.question.answers
      ? this.question.answers
      : [this.emptyAnswerColumn, this.emptyAnswerRow];
    this.sortAnswersByOrder(this.question.answers);
    this.fieldNumber = this.question.answers.length;
    this.buildFields();
    this.questionMatrixForm.valueChanges.subscribe((x) => {
      this.saveFormService.formValueChanged(true);
      this.question.localizedNames = this.saveFormService.mapFormvalueToQuestion(
        this.questionMatrixForm.value,
        this.partOfTitleControlName
      );
    });
  }

  createQuestionForm() {
    this.questionMatrixForm = this.questionFormBuilder.group({});
  }

  deleteColsInputGroup(item: Array<string>) {
    const index = this.columnsPropertiesArray.findIndex((i) => i === item);
    if (index === -1) {
      return;
    }
    this.columnsPropertiesArray.splice(index, 1);
    item.forEach((x) => {
      this.questionMatrixForm.removeControl(x);
    });
  }

  deleteRowsInputGroup(item: Array<string>) {
    const index = this.rowsPropertiesArray.findIndex((i) => i === item);
    if (index === -1) {
      return;
    }
    this.rowsPropertiesArray.splice(index, 1);
    item.forEach((x) => {
      this.questionMatrixForm.removeControl(x);
    });
  }

  getIsMatrixCheckbox(): boolean {
    return this.question.answerType === QuestionType.MatrixCheckbox;
  }

  canShowFormControlError(formControlName: string): boolean {
    return (
      this.saveClicked &&
      this.saveFormService.canShowFormControlFieldError(
        this.questionMatrixForm,
        formControlName
      )
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  private addInputSubscription() {
    this.saveFormService.addInputButtonClicked$.subscribe((s) =>
      this.addField(s.isColumn)
    );
  }

  private addField(isColumn: boolean) {
    this.fieldNumber++;
    const partOfControlName = isColumn
      ? this.partOfMatrixColControlName
      : this.partOfMatrixRowControlName;
    this.saveFormService.addInputToForm(
      this.questionMatrixForm,
      partOfControlName,
      this.fieldNumber
    );
    const choicesLangArray: Array<string> = this.saveFormService.mapFormGroupControlToPropArray(
      this.questionMatrixForm,
      `${partOfControlName}_${this.fieldNumber}_`
    );
    isColumn
      ? this.columnsPropertiesArray.push(choicesLangArray)
      : this.rowsPropertiesArray.push(choicesLangArray);
    this.saveFormService.addValidationToLocalizedControls(
      this.questionMatrixForm,
      partOfControlName
    );
    this.setPlaceholders();
  }

  private buildFields() {
    this.saveFormService.addAllLocalizedNameToFormGroup(
      this.questionMatrixForm,
      this.partOfTitleControlName,
      this.question.localizedNames
    );
    this.titlePropertiesArray = this.saveFormService.mapFormGroupControlToPropArray(
      this.questionMatrixForm,
      this.partOfTitleControlName
    );
    this.saveFormService.addValidationToLocalizedControls(
      this.questionMatrixForm,
      this.partOfTitleControlName
    );
    this.getMatrixFormColumns();
    this.getMatrixFormRows();
    this.setPlaceholders();
  }

  private getMatrixFormRows() {
    const rowAnswers = this.getAnswerRows(this.question.answers);
    for (let index = 0; index < rowAnswers.length; index++) {
      this.saveFormService.addAllLocalizedNameToFormGroup(
        this.questionMatrixForm,
        `${this.partOfMatrixRowControlName}_${index}_`,
        rowAnswers[index].localizedNames
      );
      const choicesRowLangArray: Array<string> = this.saveFormService.mapFormGroupControlToPropArray(
        this.questionMatrixForm,
        `${this.partOfMatrixRowControlName}_${index}_`
      );
      this.rowsPropertiesArray.push(choicesRowLangArray);
      this.saveFormService.addValidationToLocalizedControls(
        this.questionMatrixForm,
        this.partOfMatrixRowControlName
      );
    }
  }

  private getMatrixFormColumns() {
    const columnAnswers: Array<Answer> = this.getAnswerColumns(
      this.question.answers
    );
    for (let index = 0; index < columnAnswers.length; index++) {
      this.saveFormService.addAllLocalizedNameToFormGroup(
        this.questionMatrixForm,
        `${this.partOfMatrixColControlName}_${index}_`,
        columnAnswers[index].localizedNames
      );
      const choicesColLangArray: Array<string> = this.saveFormService.mapFormGroupControlToPropArray(
        this.questionMatrixForm,
        `${this.partOfMatrixColControlName}_${index}_`
      );
      this.columnsPropertiesArray.push(choicesColLangArray);
      this.saveFormService.addValidationToLocalizedControls(
        this.questionMatrixForm,
        this.partOfMatrixRowControlName
      );
    }
  }

  private saveMatrixForm(isAddForm: boolean) {
    this.saveClicked = true;
    TimerHelper.timer(
      this.appData.configData.displayTooltipTimeMilliseconds,
      () => (this.saveClicked = false)
    );
    if (this.questionMatrixForm.invalid) {
      return;
    }
    this.mapMatrixQuestionFromForm();
    this.createUppdateQuestion(isAddForm);
  }

  private mapMatrixQuestionFromForm() {
    this.question.localizedNames = this.saveFormService.mapFormvalueToQuestion(
      this.questionMatrixForm.value,
      this.partOfTitleControlName
    );
    const answersColumnsLocalizedNames: Array<LocalizedName> = this.saveFormService.mapFormvalueToQuestion(
      this.questionMatrixForm.value,
      this.partOfMatrixColControlName
    );
    const answersRowsLocalizedNames: Array<LocalizedName> = this.saveFormService.mapFormvalueToQuestion(
      this.questionMatrixForm.value,
      this.partOfMatrixRowControlName
    );
    const joinedColumnsLocalizedNames = this.saveFormService.joinLocalizedNames(
      answersColumnsLocalizedNames
    );
    const joinedRowsLocalizedNames = this.saveFormService.joinLocalizedNames(
      answersRowsLocalizedNames
    );
    this.updateMatrixColumnsAnswers(joinedColumnsLocalizedNames);
    this.updateMatrixRowsAnswers(joinedRowsLocalizedNames);
  }

  private createUppdateQuestion(isAddForm: boolean) {
    if (isAddForm) {
      this.questionService.create(this.question).subscribe((x) => {
        this.saveFormService.closeForm(this.question);
      });
    } else {
      this.questionService.update(this.question).subscribe(
        (x) => {
          this.saveFormService.closeForm(this.question);
        },
        (err) => {
          this.toastr.error(err.error, 'Error');
        }
      );
    }
  }

  private updateMatrixColumnsAnswers(
    answersLocalizedNames: Array<Array<LocalizedName>>
  ) {
    const answerColumns = this.getAnswerColumns(this.question.answers);
    answerColumns.forEach((x, i) => {
      x.localizedNames = answersLocalizedNames[i];
      if (isNaN(x.order)) {
        x.order = this.calculateOrder();
      }
    });
    if (answerColumns.length < answersLocalizedNames.length) {
      this.addMatrixColumnsAnswer(answerColumns.length, answersLocalizedNames);
    } else if (answerColumns.length > answersLocalizedNames.length) {
      this.removeMatrixColumnsAnswer(answersLocalizedNames.length);
    }
  }

  private addMatrixColumnsAnswer(
    startIndex: number,
    items: Array<Array<LocalizedName>>
  ) {
    for (let index = startIndex; index < items.length; index++) {
      this.question.answers.push({
        localizedNames: items[index],
        compositeAnswerPortion: CompositeAnswerPortion.Column,
        order: this.calculateOrder(),
      });
    }
  }

  private removeMatrixColumnsAnswer(startIndex: number) {
    const answerColumns = this.getAnswerColumns(this.question.answers);
    for (let index = startIndex; index < answerColumns.length; index++) {
      const answerIndex = this.question.answers.findIndex(
        (x) => x.id === answerColumns[index].id
      );
      if (answerIndex === -1) {
        return;
      }
      this.question.answers.splice(answerIndex, 1);
    }
  }

  private updateMatrixRowsAnswers(
    answersLocalizedNames: Array<Array<LocalizedName>>
  ) {
    const answerRows = this.getAnswerRows(this.question.answers);
    answerRows.forEach((x, i) => {
      x.localizedNames = answersLocalizedNames[i];
      if (isNaN(x.order)) {
        x.order = this.calculateOrder();
      }
    });
    if (answerRows.length < answersLocalizedNames.length) {
      this.addMatrixRowsAnswer(answerRows.length, answersLocalizedNames);
    } else if (answerRows.length > answersLocalizedNames.length) {
      this.removeMatrixRowsAnswer(answersLocalizedNames.length);
    }
  }

  private removeMatrixRowsAnswer(startIndex: number) {
    const answerRows = this.getAnswerRows(this.question.answers);
    for (let index = startIndex; index < answerRows.length; index++) {
      const answerIndex = this.question.answers.findIndex(
        (x) => x.id === answerRows[index].id
      );
      if (answerIndex === -1) {
        return;
      }
      this.question.answers.splice(answerIndex, 1);
    }
  }

  private addMatrixRowsAnswer(
    startIndex: number,
    items: Array<Array<LocalizedName>>
  ) {
    for (let index = startIndex; index < items.length; index++) {
      this.question.answers.push({
        localizedNames: items[index],
        compositeAnswerPortion: CompositeAnswerPortion.Row,
        order: this.calculateOrder(),
      });
    }
  }

  private calculateOrder(): number {
    const orders = this.question.answers
      .filter((o) => o.order)
      .map((a) => a.order);
    return orders.length > 0 ? Math.max(...orders) + 1 : 1;
  }

  private getAnswerRows(answers: Array<Answer>): Array<Answer> {
    return answers.filter(
      (x) => x.compositeAnswerPortion === CompositeAnswerPortion.Row
    );
  }

  private getAnswerColumns(answers: Array<Answer>): Array<Answer> {
    return answers.filter(
      (x) => x.compositeAnswerPortion === CompositeAnswerPortion.Column
    );
  }

  private sortAnswersByOrder(answers: Array<Answer>) {
    return answers.sort((a, b) => a.order - b.order);
  }

  private setPlaceholders() {
    const allControls = this.saveFormService.getAllFormControlsFields(
      this.questionMatrixForm.controls
    );
    const titleControls = this.saveFormService.getControlsByControlName(
      this.partOfTitleControlName,
      allControls
    );
    const columnsControls = this.saveFormService.getControlsByControlName(
      this.partOfMatrixColControlName,
      allControls
    );
    const rowsControls = this.saveFormService.getControlsByControlName(
      this.partOfMatrixRowControlName,
      allControls
    );
    this.titlePlaceholderArray = this.saveFormService.setPlaceholderArrayByPropName(
      titleControls
    );
    this.columnsPlaceholderArray = this.saveFormService.setPlaceholderArrayByPropName(
      columnsControls
    );
    this.rowsPlaceholderArray = this.saveFormService.setPlaceholderArrayByPropName(
      rowsControls
    );
  }
}
